import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Logo } from './common/Logo';

// Gen-Z appealing color palettes - Modern, sleek, trendy
const colorPalettes = {
  'Neon Cyber': {
    primary: ['#00D9FF', '#0099FF'],
    secondary: '#FF00E5',
    accent: '#39FF14',
    bgDark: '#000000',
    bgCard: '#0A0A0A',
    bgLight: '#FAFAFA',
    textDark: '#FFFFFF',
    textMuted: '#888888',
    textLight: '#0A0A0A',
    gradient: 'linear-gradient(135deg, #00D9FF 0%, #FF00E5 50%, #39FF14 100%)',
    description: 'Cyberpunk vibes - Electric cyan, hot pink, neon green'
  },
  'Midnight Aurora': {
    primary: ['#6366F1', '#8B5CF6'],
    secondary: '#EC4899',
    accent: '#14F195',
    bgDark: '#09090B',
    bgCard: '#18181B',
    bgLight: '#FAFAFA',
    textDark: '#FAFAFA',
    textMuted: '#71717A',
    textLight: '#09090B',
    gradient: 'linear-gradient(135deg, #6366F1 0%, #EC4899 50%, #14F195 100%)',
    description: 'Like Vercel/Linear - Deep blacks, aurora gradients'
  },
  'Sunset Haze': {
    primary: ['#F97316', '#FB923C'],
    secondary: '#E11D48',
    accent: '#FBBF24',
    bgDark: '#0C0A09',
    bgCard: '#1C1917',
    bgLight: '#FFFBEB',
    textDark: '#FAFAF9',
    textMuted: '#78716C',
    textLight: '#0C0A09',
    gradient: 'linear-gradient(135deg, #F97316 0%, #E11D48 50%, #FBBF24 100%)',
    description: 'Warm & bold - Sunset oranges, rose, golden yellow'
  },
  'Arctic Frost': {
    primary: ['#06B6D4', '#22D3EE'],
    secondary: '#3B82F6',
    accent: '#67E8F9',
    bgDark: '#030712',
    bgCard: 'rgba(15, 23, 42, 0.6)',
    bgLight: '#F0F9FF',
    textDark: '#F8FAFC',
    textMuted: '#94A3B8',
    textLight: '#020617',
    gradient: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
    description: 'Frosted glass - Icy cyan, deep navy, premium glass effects'
  },
  'Lavender Dreams': {
    primary: ['#A855F7', '#C084FC'],
    secondary: '#F472B6',
    accent: '#67E8F9',
    bgDark: '#0D0D0D',
    bgCard: '#1A1A2E',
    bgLight: '#FAF5FF',
    textDark: '#F5F3FF',
    textMuted: '#A1A1AA',
    textLight: '#1E1B4B',
    gradient: 'linear-gradient(135deg, #A855F7 0%, #F472B6 50%, #67E8F9 100%)',
    description: 'Dreamy & soft - Purple haze, pink glow, cyan spark'
  },
  'Monochrome Pro': {
    primary: ['#FFFFFF', '#E5E5E5'],
    secondary: '#A3A3A3',
    accent: '#FAFAFA',
    bgDark: '#000000',
    bgCard: '#0A0A0A',
    bgLight: '#FFFFFF',
    textDark: '#FFFFFF',
    textMuted: '#525252',
    textLight: '#000000',
    gradient: 'linear-gradient(135deg, #FFFFFF 0%, #A3A3A3 50%, #525252 100%)',
    description: 'Pure minimal - Like Apple, stark black & white'
  }
};

type PaletteName = keyof typeof colorPalettes;

const ColorPreview: React.FC = () => {
  const [selectedPalette, setSelectedPalette] = useState<PaletteName>('Arctic Frost');
  const [isDarkMode, setIsDarkMode] = useState(true);

  const palette = colorPalettes[selectedPalette];
  const bgColor = isDarkMode ? palette.bgDark : palette.bgLight;
  const cardBg = isDarkMode ? palette.bgCard : 'rgba(255,255,255,0.8)';
  const textColor = isDarkMode ? palette.textDark : palette.textLight;
  const mutedColor = isDarkMode ? palette.textMuted : '#6B7280';

  // Enhanced glass effect for Arctic Frost
  const isGlassy = selectedPalette === 'Arctic Frost';
  const glassStyle = isGlassy ? {
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  } : {};

  return (
    <div
      className="min-h-screen transition-all duration-500 relative overflow-hidden"
      style={{ backgroundColor: bgColor }}
    >
      {/* Animated gradient mesh background for Arctic Frost */}
      {isGlassy && isDarkMode && (
        <>
          <div
            className="fixed inset-0 opacity-40 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse at 20% 20%, rgba(6, 182, 212, 0.15) 0%, transparent 50%),
                radial-gradient(ellipse at 80% 80%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
                radial-gradient(ellipse at 50% 50%, rgba(103, 232, 249, 0.08) 0%, transparent 60%)
              `
            }}
          />
          {/* Noise texture overlay */}
          <div
            className="fixed inset-0 opacity-[0.015] pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            }}
          />
        </>
      )}

      {/* Ambient glow effects */}
      <div
        className="fixed top-0 left-1/4 w-96 h-96 rounded-full blur-[128px] pointer-events-none transition-opacity duration-500"
        style={{
          background: palette.primary[0],
          opacity: isGlassy ? 0.4 : 0.3
        }}
      />
      <div
        className="fixed bottom-0 right-1/4 w-96 h-96 rounded-full blur-[128px] pointer-events-none transition-opacity duration-500"
        style={{
          background: palette.secondary,
          opacity: isGlassy ? 0.3 : 0.2
        }}
      />
      {isGlassy && (
        <div
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-[150px] opacity-20 pointer-events-none"
          style={{ background: palette.accent }}
        />
      )}

      {/* Palette Selector - Glassmorphic */}
      <div
        className="fixed top-4 left-4 z-50 p-5 rounded-2xl border"
        style={{
          backgroundColor: isGlassy
            ? (isDarkMode ? 'rgba(15, 23, 42, 0.7)' : 'rgba(255,255,255,0.7)')
            : (isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
          borderColor: isGlassy
            ? (isDarkMode ? 'rgba(103, 232, 249, 0.2)' : 'rgba(6, 182, 212, 0.3)')
            : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'),
          backdropFilter: isGlassy ? 'blur(20px) saturate(180%)' : 'blur(12px)',
          WebkitBackdropFilter: isGlassy ? 'blur(20px) saturate(180%)' : 'blur(12px)',
          boxShadow: isGlassy ? '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255,255,255,0.1)' : 'none'
        }}
      >
        <div className="flex items-center justify-between mb-5">
          <span style={{ color: textColor }} className="font-semibold text-sm tracking-wide uppercase">Theme</span>
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            className="px-4 py-1.5 rounded-full text-xs font-semibold transition-all hover:scale-105"
            style={{
              background: palette.gradient,
              color: '#000'
            }}
          >
            {isDarkMode ? 'Dark' : 'Light'}
          </button>
        </div>
        <div className="space-y-1.5">
          {(Object.keys(colorPalettes) as PaletteName[]).map((name) => (
            <button
              key={name}
              onClick={() => setSelectedPalette(name)}
              className="w-full text-left px-3 py-2.5 rounded-xl transition-all hover:scale-[1.02]"
              style={{
                backgroundColor: selectedPalette === name
                  ? (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)')
                  : 'transparent',
                color: textColor,
                boxShadow: selectedPalette === name
                  ? `0 0 20px ${colorPalettes[name].primary[0]}40`
                  : 'none'
              }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-6 h-6 rounded-lg"
                  style={{ background: colorPalettes[name].gradient }}
                />
                <span className="text-sm font-medium">{name}</span>
              </div>
            </button>
          ))}
        </div>
        <p className="mt-5 text-xs leading-relaxed" style={{ color: mutedColor }}>
          {palette.description}
        </p>
      </div>

      {/* Preview Content */}
      <div className="max-w-6xl mx-auto px-6 py-24 ml-72">
        {/* Navigation Preview */}
        <motion.nav
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex justify-between items-center mb-32 px-6 py-4 rounded-2xl border"
          style={{
            backgroundColor: isGlassy
              ? (isDarkMode ? 'rgba(15, 23, 42, 0.5)' : 'rgba(255,255,255,0.6)')
              : (isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'),
            borderColor: isGlassy
              ? (isDarkMode ? 'rgba(103, 232, 249, 0.15)' : 'rgba(6, 182, 212, 0.2)')
              : (isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'),
            backdropFilter: isGlassy ? 'blur(20px) saturate(180%)' : 'blur(12px)',
            WebkitBackdropFilter: isGlassy ? 'blur(20px) saturate(180%)' : 'blur(12px)',
            boxShadow: isGlassy ? '0 4px 24px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255,255,255,0.05)' : 'none'
          }}
        >
          <div className="flex items-center gap-3">
            <Logo variant="app-icon" size={36} isDarkMode={isDarkMode} />
            <span className="text-lg font-semibold tracking-tight" style={{ color: textColor }}>vidya.ai</span>
          </div>
          <div className="flex items-center gap-6">
            <span className="text-sm cursor-pointer hover:opacity-100 transition-opacity" style={{ color: mutedColor }}>Features</span>
            <span className="text-sm cursor-pointer hover:opacity-100 transition-opacity" style={{ color: mutedColor }}>Pricing</span>
            <span className="text-sm cursor-pointer hover:opacity-100 transition-opacity" style={{ color: mutedColor }}>Sign In</span>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-5 py-2 rounded-full text-sm font-medium transition-all"
              style={{
                background: palette.gradient,
                color: '#000',
                boxShadow: `0 0 30px ${palette.primary[0]}40`
              }}
            >
              Get Started
            </motion.button>
          </div>
        </motion.nav>

        {/* Hero Preview */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="text-center mb-32"
        >
          {/* Pill badge */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 border"
            style={{
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
              borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'
            }}
          >
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-2 h-2 rounded-full"
              style={{ background: palette.gradient }}
            />
            <span className="text-sm font-medium" style={{ color: mutedColor }}>
              Your AI tutor that never forgets
            </span>
          </motion.div>

          {/* Main headline with gradient text */}
          <h1 className="text-7xl md:text-8xl font-bold mb-6 leading-[0.95] tracking-tight">
            <span style={{ color: textColor }}>Ask once.</span>
            <br />
            <span
              style={{
                background: palette.gradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                filter: `drop-shadow(0 0 40px ${palette.primary[0]}60)`
              }}
            >
              Remember forever.
            </span>
          </h1>

          <p
            className="text-xl mb-12 max-w-xl mx-auto leading-relaxed"
            style={{ color: mutedColor }}
          >
            Stop re-explaining yourself to AI. Vidya remembers your books, your notes, your journey.
          </p>

          {/* CTA buttons */}
          <div className="flex justify-center gap-4">
            <motion.button
              whileHover={{ scale: 1.02, boxShadow: `0 0 50px ${palette.primary[0]}50` }}
              whileTap={{ scale: 0.98 }}
              className="px-8 py-4 rounded-full font-semibold flex items-center gap-3 transition-all"
              style={{
                background: palette.gradient,
                color: '#000',
                boxShadow: `0 0 40px ${palette.primary[0]}30`
              }}
            >
              Start Learning Free
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02, backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}
              whileTap={{ scale: 0.98 }}
              className="px-8 py-4 rounded-full font-semibold transition-all border"
              style={{
                color: textColor,
                borderColor: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
                backgroundColor: 'transparent'
              }}
            >
              Watch Demo
            </motion.button>
          </div>
        </motion.div>

        {/* Bento Grid Feature Cards */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-3 gap-4 mb-32"
        >
          {/* Large card */}
          <motion.div
            whileHover={{ scale: 1.01, y: -2 }}
            className="col-span-2 p-8 rounded-3xl border relative overflow-hidden group"
            style={{
              backgroundColor: isGlassy
                ? (isDarkMode ? 'rgba(15, 23, 42, 0.5)' : 'rgba(255,255,255,0.6)')
                : cardBg,
              borderColor: isGlassy
                ? (isDarkMode ? 'rgba(103, 232, 249, 0.12)' : 'rgba(6, 182, 212, 0.2)')
                : (isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'),
              backdropFilter: isGlassy ? 'blur(20px) saturate(180%)' : 'blur(8px)',
              WebkitBackdropFilter: isGlassy ? 'blur(20px) saturate(180%)' : 'blur(8px)',
              boxShadow: isGlassy
                ? '0 8px 32px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255,255,255,0.08)'
                : 'none'
            }}
          >
            {/* Glass reflection effect */}
            {isGlassy && (
              <div
                className="absolute inset-0 opacity-30 pointer-events-none"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%, transparent 100%)'
                }}
              />
            )}
            <div
              className="absolute top-0 right-0 w-64 h-64 rounded-full blur-[100px] opacity-20 group-hover:opacity-40 transition-opacity"
              style={{ background: palette.primary[0] }}
            />
            <div className="relative z-10">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl mb-6"
                style={{
                  background: isGlassy
                    ? `linear-gradient(135deg, ${palette.primary[0]}30, ${palette.primary[1]}20)`
                    : `${palette.primary[0]}15`,
                  boxShadow: isGlassy ? `0 4px 16px ${palette.primary[0]}30` : 'none'
                }}
              >
                🧠
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ color: textColor }}>
                Memory that never fades
              </h3>
              <p className="text-base leading-relaxed max-w-md" style={{ color: mutedColor }}>
                Unlike ChatGPT, Vidya remembers every highlight, every note, every question you've asked.
              </p>
            </div>
          </motion.div>

          {/* Small card */}
          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="p-6 rounded-3xl border relative overflow-hidden group"
            style={{
              backgroundColor: isGlassy
                ? (isDarkMode ? 'rgba(15, 23, 42, 0.5)' : 'rgba(255,255,255,0.6)')
                : cardBg,
              borderColor: isGlassy
                ? (isDarkMode ? 'rgba(103, 232, 249, 0.12)' : 'rgba(6, 182, 212, 0.2)')
                : (isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'),
              backdropFilter: isGlassy ? 'blur(20px) saturate(180%)' : 'blur(8px)',
              WebkitBackdropFilter: isGlassy ? 'blur(20px) saturate(180%)' : 'blur(8px)',
              boxShadow: isGlassy
                ? '0 8px 32px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255,255,255,0.08)'
                : 'none'
            }}
          >
            {isGlassy && (
              <div
                className="absolute inset-0 opacity-30 pointer-events-none"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%, transparent 100%)'
                }}
              />
            )}
            <div
              className="absolute top-0 right-0 w-32 h-32 rounded-full blur-[60px] opacity-20 group-hover:opacity-40 transition-opacity"
              style={{ background: palette.secondary }}
            />
            <div className="relative z-10">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl mb-4"
                style={{
                  background: isGlassy
                    ? `linear-gradient(135deg, ${palette.secondary}30, ${palette.secondary}20)`
                    : `${palette.secondary}15`,
                  boxShadow: isGlassy ? `0 4px 16px ${palette.secondary}30` : 'none'
                }}
              >
                📚
              </div>
              <h3 className="text-lg font-bold mb-2" style={{ color: textColor }}>
                Your curriculum
              </h3>
              <p className="text-sm" style={{ color: mutedColor }}>
                AI that understands your exact textbooks
              </p>
            </div>
          </motion.div>

          {/* Small card */}
          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="p-6 rounded-3xl border relative overflow-hidden group"
            style={{
              backgroundColor: isGlassy
                ? (isDarkMode ? 'rgba(15, 23, 42, 0.5)' : 'rgba(255,255,255,0.6)')
                : cardBg,
              borderColor: isGlassy
                ? (isDarkMode ? 'rgba(103, 232, 249, 0.12)' : 'rgba(6, 182, 212, 0.2)')
                : (isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'),
              backdropFilter: isGlassy ? 'blur(20px) saturate(180%)' : 'blur(8px)',
              WebkitBackdropFilter: isGlassy ? 'blur(20px) saturate(180%)' : 'blur(8px)',
              boxShadow: isGlassy
                ? '0 8px 32px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255,255,255,0.08)'
                : 'none'
            }}
          >
            {isGlassy && (
              <div
                className="absolute inset-0 opacity-30 pointer-events-none"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%, transparent 100%)'
                }}
              />
            )}
            <div className="relative z-10">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl mb-4"
                style={{
                  background: isGlassy
                    ? `linear-gradient(135deg, ${palette.accent}30, ${palette.accent}20)`
                    : `${palette.accent}15`,
                  boxShadow: isGlassy ? `0 4px 16px ${palette.accent}30` : 'none'
                }}
              >
                ⚡
              </div>
              <h3 className="text-lg font-bold mb-2" style={{ color: textColor }}>
                24/7 Available
              </h3>
              <p className="text-sm" style={{ color: mutedColor }}>
                Your tutor never sleeps
              </p>
            </div>
          </motion.div>

          {/* Large card */}
          <motion.div
            whileHover={{ scale: 1.01, y: -2 }}
            className="col-span-2 p-8 rounded-3xl border relative overflow-hidden group"
            style={{
              backgroundColor: isGlassy
                ? (isDarkMode ? 'rgba(15, 23, 42, 0.5)' : 'rgba(255,255,255,0.6)')
                : cardBg,
              borderColor: isGlassy
                ? (isDarkMode ? 'rgba(103, 232, 249, 0.12)' : 'rgba(6, 182, 212, 0.2)')
                : (isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'),
              backdropFilter: isGlassy ? 'blur(20px) saturate(180%)' : 'blur(8px)',
              WebkitBackdropFilter: isGlassy ? 'blur(20px) saturate(180%)' : 'blur(8px)',
              boxShadow: isGlassy
                ? '0 8px 32px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255,255,255,0.08)'
                : 'none'
            }}
          >
            {isGlassy && (
              <div
                className="absolute inset-0 opacity-30 pointer-events-none"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%, transparent 100%)'
                }}
              />
            )}
            <div
              className="absolute bottom-0 left-0 w-64 h-64 rounded-full blur-[100px] opacity-15 group-hover:opacity-30 transition-opacity"
              style={{ background: palette.accent }}
            />
            <div className="relative z-10">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl mb-6"
                style={{
                  background: isGlassy
                    ? `linear-gradient(135deg, ${palette.accent}30, ${palette.accent}20)`
                    : `${palette.accent}15`,
                  boxShadow: isGlassy ? `0 4px 16px ${palette.accent}30` : 'none'
                }}
              >
                💬
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ color: textColor }}>
                Context-aware answers
              </h3>
              <p className="text-base leading-relaxed max-w-md" style={{ color: mutedColor }}>
                "Based on what you highlighted in Chapter 4..." — responses tailored to your learning journey.
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Stats with glow */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex justify-center gap-20 mb-32"
        >
          {[
            { value: '10K+', label: 'Students' },
            { value: '50K+', label: 'Questions' },
            { value: '100+', label: 'Books' },
            { value: '4.9★', label: 'Rating' }
          ].map((stat, i) => (
            <div key={i} className="text-center">
              <div
                className="text-5xl font-bold mb-2"
                style={{
                  background: palette.gradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  filter: `drop-shadow(0 0 20px ${palette.primary[0]}40)`
                }}
              >
                {stat.value}
              </div>
              <div className="text-sm font-medium" style={{ color: mutedColor }}>
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Final CTA with gradient border */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="relative rounded-3xl p-[1px] overflow-hidden"
          style={{
            background: isGlassy
              ? `linear-gradient(135deg, ${palette.primary[0]}60, ${palette.secondary}40, ${palette.accent}60)`
              : palette.gradient
          }}
        >
          <div
            className="rounded-3xl p-16 text-center relative overflow-hidden"
            style={{
              backgroundColor: isGlassy
                ? (isDarkMode ? 'rgba(3, 7, 18, 0.9)' : 'rgba(240, 249, 255, 0.9)')
                : bgColor,
              backdropFilter: isGlassy ? 'blur(20px)' : 'none',
              WebkitBackdropFilter: isGlassy ? 'blur(20px)' : 'none',
            }}
          >
            {/* Inner glass reflection */}
            {isGlassy && (
              <div
                className="absolute inset-0 opacity-20 pointer-events-none"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 40%, transparent 100%)'
                }}
              />
            )}
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-[120px] opacity-20"
              style={{ background: palette.gradient }}
            />
            <div className="relative z-10">
              <h2 className="text-5xl font-bold mb-4 tracking-tight" style={{ color: textColor }}>
                Ready to ace your exams?
              </h2>
              <p className="mb-10 text-lg" style={{ color: mutedColor }}>
                Free to start. No credit card required.
              </p>
              <motion.button
                whileHover={{ scale: 1.02, boxShadow: `0 0 60px ${palette.primary[0]}50` }}
                whileTap={{ scale: 0.98 }}
                className="px-10 py-5 rounded-full font-semibold text-lg transition-all"
                style={{
                  background: palette.gradient,
                  color: isGlassy ? '#030712' : '#000',
                  boxShadow: isGlassy
                    ? `0 0 40px ${palette.primary[0]}40, inset 0 1px 0 rgba(255,255,255,0.3)`
                    : `0 0 40px ${palette.primary[0]}30`
                }}
              >
                Get Started Free →
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Color swatches */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-20"
        >
          <h3 className="text-lg font-semibold mb-6 text-center" style={{ color: mutedColor }}>
            Palette Colors
          </h3>
          <div className="flex justify-center gap-6 flex-wrap">
            <div className="text-center">
              <div
                className="w-16 h-16 rounded-2xl mb-2 mx-auto"
                style={{ background: palette.gradient, boxShadow: `0 0 30px ${palette.primary[0]}40` }}
              />
              <span className="text-xs" style={{ color: mutedColor }}>Primary</span>
            </div>
            <div className="text-center">
              <div
                className="w-16 h-16 rounded-2xl mb-2 mx-auto"
                style={{ backgroundColor: palette.secondary, boxShadow: `0 0 30px ${palette.secondary}40` }}
              />
              <span className="text-xs" style={{ color: mutedColor }}>Secondary</span>
            </div>
            <div className="text-center">
              <div
                className="w-16 h-16 rounded-2xl mb-2 mx-auto"
                style={{ backgroundColor: palette.accent, boxShadow: `0 0 30px ${palette.accent}40` }}
              />
              <span className="text-xs" style={{ color: mutedColor }}>Accent</span>
            </div>
            <div className="text-center">
              <div
                className="w-16 h-16 rounded-2xl mb-2 mx-auto border"
                style={{
                  backgroundColor: cardBg,
                  borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                }}
              />
              <span className="text-xs" style={{ color: mutedColor }}>Card BG</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ColorPreview;
